/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * postLogin
 * ---------
 * Shared "what happens after any successful login" bootstrap logic (D-04).
 *
 * This is the single source of truth for: storing `userData` and signalling
 * NotificationProvider to reinitialise, so both `SignInPage.js`'s
 * password-login path and `SsoCallbackPage.js`'s SSO-login path behave
 * identically.
 *
 * `SignInPage.js` itself is NOT modified to call this helper — refactoring
 * its already-working inline block is explicitly out of scope for this plan
 * (D-04 only requires no NEW parallel logic for the SSO path).
 */

import { store } from "./storage_helper";

/**
 * runPostLoginFlow
 * -----------------
 * Given a successful login response `data` (any shape with a `message` key
 * plus arbitrary other fields — e.g. `{ message, user_id, created, user_name,
 * first_name, last_name }`), performs the exact same bootstrap sequence
 * SignInPage.js's password-login path already performs:
 *
 *  1. Strips `message`, stores everything else under `userData` (no field
 *     allowlist — forward-compatible with any extended response shape).
 *  2. Dispatches `arcee:auth-change` exactly once.
 *  3. Navigates to `defaultTarget`.
 *
 * @param {object} data - the full login/callback response body.
 * @param {object} opts
 * @param {function} opts.navigate - react-router's navigate function.
 * @param {string} [opts.defaultTarget="/dashboard"] - where to navigate after bootstrap.
 */
export const runPostLoginFlow = async (data, { navigate, defaultTarget = "/dashboard" }) => {
    const { message, ...userData } = data;
    store("userData", userData);

    // Signal NotificationProvider to reinitialise WS and seed state
    // for the newly logged-in user / application.
    window.dispatchEvent(new Event('arcee:auth-change'));

    navigate(defaultTarget, { replace: true });
};

export default runPostLoginFlow;
